import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import { errorCode } from '@/api/errors';
import type { components } from '@/api/qraft/schema';
import { AlertBox } from '@/common/AlertBox';
import { FolderTabs } from '@/common/FolderTabs';
import { InfoSectionContainer } from '@/common/InfoSection';
import { TabsBar } from '@/common/TabsBar';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { useAdminTabs } from '@/groups/coordinator/useAdminTabs';
import { DeletePhotoTab } from '@/photo/DeletePhotoTab';
import { UploadPhotoTab } from '@/photo/UploadPhotoTab';
import { PageContent } from '@/settings/settingsStyles';
import { GroupHeaderWrapper, Title } from '~/styles/layoutElements';
import { formatString } from '~/utils/stringFormat';

/**
 * Group types legacy offers contextual help for on this screen, and there is no
 * else branch: `themes/wbs/buddypress/assets/_attachments/avatars/index.php:20-28`
 * asks for `study-groups` and `congregations`, which
 * `WBS_Group_Types::parseGroupedTypes` (`modules/global-classes/WBS_Group_Types.php:45-56`)
 * expands to {study-helper-study-group, study-helper-explorer-group} and
 * {congregation, custom-study-group} — explorer being folded into SHSG by G4.
 * Follow-up and social groups get a plain title.
 */
const HELP_ICON_GROUP_TYPES: components['schemas']['GroupType'][] = [
  'STUDY_HELPER_STUDY_GROUP',
  'CONGREGATION',
  'CUSTOM_STUDY_GROUP',
];

/**
 * What `size_format()` rounds the server's own byte cap to. The unit is its own
 * catalogue string, not part of the number — see the `wbs.mb` note in
 * `modules/photo/UploadPhotoTab.tsx` for the three catalogue units behind it.
 */
const MAX_UPLOAD_MB = 8;

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/admin/group-avatar',
)({
  component: GroupAvatarScreen,
});

function GroupAvatarScreen() {
  const { t } = useTranslation();
  const { groupName } = Route.useParams();
  const adminTabs = useAdminTabs();
  const [activeTab, setActiveTab] = useState<'upload' | 'delete'>('upload');
  const [status, setStatus] = useState<string | null>(null);

  const { data: currentGroup } = api.getGroupBySlug.useQuery({
    path: { group_slug: groupName },
  });
  const { mutateAsync: uploadAvatar } = api.uploadGroupAvatar.useMutation();
  const { mutateAsync: deleteAvatar } = api.deleteGroupAvatar.useMutation();

  const groupId = currentGroup?.id;
  // `has_avatar` and not `Boolean(avatar_url)`: the server falls back to the
  // configured default group avatar, so `avatar_url` is never empty.
  const hasPhoto = Boolean(currentGroup?.has_avatar);

  const refreshGroup = async (): Promise<void> => {
    // The Delete tab's visibility and the sidebar's photo both read the group.
    await Promise.all([
      api.getGroupBySlug.invalidateQueries(),
      api.listMyGroups.invalidateQueries(),
    ]);
  };

  const handleUpload = async (blob: Blob): Promise<void> => {
    if (groupId === undefined) throw new Error('Group not loaded');
    await uploadAvatar({ path: { group_id: groupId }, body: { file: blob } });
    await refreshGroup();
    setStatus(t('groups.group-photo-uploaded'));
  };

  const handleDelete = async (): Promise<void> => {
    if (groupId === undefined) throw new Error('Group not loaded');
    await deleteAvatar({ path: { group_id: groupId } });
    // Both before the refresh, which flips `hasPhoto` false and so unmounts the
    // Delete pane. Legacy does not have this problem because its confirmation
    // lives in `.bp-avatar-status`, a sibling of the panes
    // (`assets/_attachments/avatars/index.php:31-34`, `bp-core/js/avatar.js:455-470`),
    // which is why the message is hoisted out of the pane here too. Legacy then
    // leaves an empty pane — it never calls `setView` — where we show Upload (D14).
    setStatus(t('groups.group-photo-deleted'));
    setActiveTab('upload');
    await refreshGroup();
  };

  /**
   * Divergence from §6.6's "map the six `group_avatar_*` codes to their §11
   * keys": only `too_large` is mapped, because §11 mints only one key
   * (`groups.photo-too-big`) and the other five codes are server-side backstops
   * this screen cannot provoke — the picker rejects anything over legacy's
   * 5,120,000 bytes, the input is `accept="image/*"`, and the cropper always
   * re-encodes to a canvas PNG capped at 1024px, so `missing_file`, `empty`,
   * `invalid_image`, `unsupported_format` and `too_many_pixels` have no path
   * from this UI. They fall through to the generic failure with the request
   * cap's `payload_too_large`, rather than minting catalogue nobody can reach.
   */
  const uploadErrorMessage = (failure: unknown): string | undefined =>
    errorCode(failure) === 'group_avatar_too_large'
      ? formatString(
          t('groups.photo-too-big'),
          `${MAX_UPLOAD_MB} ${t('wbs.mb')}`,
        )
      : undefined;

  const deleteErrorMessage = (): string =>
    t('groups.group-photo-delete-failed');

  const photoTabs: { id: 'upload' | 'delete'; label: string }[] = [
    { id: 'upload', label: t('wbs.upload') },
    ...(hasPhoto
      ? [{ id: 'delete' as const, label: t('actions.delete') }]
      : []),
  ];

  const handleTabChange = (tab: 'upload' | 'delete') => {
    // A confirmation belongs to the action that produced it, not to the tab the
    // user moves on to.
    setStatus(null);
    setActiveTab(tab);
  };

  const groupType = currentGroup?.group_type;
  const showHelpIcon = !!groupType && HELP_ICON_GROUP_TYPES.includes(groupType);

  return (
    <PageContent>
      <TabsBar tabs={adminTabs} base="/groups/$groupName/admin" />
      <GroupHeaderWrapper>
        {showHelpIcon ? (
          <TitleWithQuestion title={t('wbs.photo')} />
        ) : (
          <Title>{t('wbs.photo')}</Title>
        )}
      </GroupHeaderWrapper>
      <InfoSectionContainer>
        {status && <AlertBox variant="success">{status}</AlertBox>}
        <FolderTabs
          tabs={photoTabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
        <UploadWrapper>
          {activeTab === 'upload' && (
            <UploadPhotoTab
              // Legacy shows the "300px, and use the delete tab" warning only
              // when the object already has an avatar — `bp-core/js/avatar.js:555-560`
              // reads the delete nav item's `hide`, which is `(int) ! $has_avatar`
              // (`bp-core-attachments.php:928`). Ungated it would point every
              // group at a Delete tab that is correctly not there.
              //
              // D12: the gating is parity, the dismissal is not. Legacy drops
              // the warning on any nav click (`avatar.js:608` `toggleView` ->
              // `removeWarning`), so it survives neither a tab switch nor the
              // move into the cropper. Here `showNotice` is a prop threaded to
              // both panes, not a mutable view, so it persists — erring toward
              // showing the notice more, not less.
              showNotice={hasPhoto}
              onUpload={handleUpload}
              resolveErrorMessage={uploadErrorMessage}
            />
          )}
          {activeTab === 'delete' && hasPhoto && (
            <DeletePhotoTab
              infoMessage={t('groups.delete-group-photo-info')}
              buttonLabel={t('groups.delete-group-profile-photo')}
              onDelete={handleDelete}
              resolveErrorMessage={deleteErrorMessage}
            />
          )}
        </UploadWrapper>
      </InfoSectionContainer>
    </PageContent>
  );
}

const UploadWrapper = styled.div`
  margin-top: 20px;
`;
